/**
 * The landing view: their questions first, then any sandbox access issued to
 * them, then the library behind them.
 */

import {
  DOCUMENT_TIER,
  type ListPortalAccessResponse,
  type ListPortalDocumentsResponse,
  type PortalDocument,
} from '@insuredhq/contract';

import { DocumentGroup } from './DocumentGroup';
import { NdaModal } from './NdaModal';
import { Notice } from './Notice';
import { RequestItems } from './RequestItems';
import { SandboxAccess } from './SandboxAccess';
import {
  LIBRARY_HEADING,
  NDA_BOUNDARY_CHIP,
  NDA_BOUNDARY_NOTE,
  SIGN_OUT_NOTE,
  TIER_1_EMPTY,
  TIER_1_HEADING,
  TIER_2_EMPTY,
  TIER_2_HEADING,
} from '../copy';
import type { RevealOutcome } from '../hooks/usePortal';

interface PortalViewProps {
  data: ListPortalDocumentsResponse;
  notice: string | null;
  busy: boolean;
  /** True while the undertaking is open, for a document or a credential. */
  ndaOpen: boolean;
  openingId: string | null;
  access: ListPortalAccessResponse | null;
  onOpen: (document: PortalDocument) => void;
  onAcceptNda: () => void;
  onDismissNda: () => void;
  onRevealCredential: (credentialId: string) => Promise<RevealOutcome>;
  onRequestCredentialNda: (credentialId: string) => void;
}

export function PortalView({
  data,
  notice,
  busy,
  ndaOpen,
  openingId,
  access,
  onOpen,
  onAcceptNda,
  onDismissNda,
  onRevealCredential,
  onRequestCredentialNda,
}: PortalViewProps) {
  const tierOne = data.documents.filter((item) => item.tier === DOCUMENT_TIER.TIER_1);
  const tierTwo = data.documents.filter((item) => item.tier === DOCUMENT_TIER.TIER_2);

  return (
    <>
      <Notice message={notice} />

      <RequestItems
        requestItems={data.requestItems}
        documents={data.documents}
        openingId={openingId}
        onOpen={onOpen}
      />

      <SandboxAccess
        access={access}
        onReveal={onRevealCredential}
        onRequestNda={onRequestCredentialNda}
      />

      <section className="group">
        <h2>{LIBRARY_HEADING}</h2>
        <p className="lede">{SIGN_OUT_NOTE}</p>
      </section>

      <DocumentGroup
        heading={TIER_1_HEADING}
        emptyMessage={TIER_1_EMPTY}
        documents={tierOne}
        openingId={openingId}
        onOpen={onOpen}
      />

      {/* The NDA line: everything above releases on email verification,
          everything below requires the undertaking. */}
      <div className="nda-boundary">
        <div className="nda-boundary-rule">
          <span className="nda-boundary-chip">{NDA_BOUNDARY_CHIP}</span>
        </div>
        <p className="nda-boundary-note">{NDA_BOUNDARY_NOTE}</p>
      </div>

      <DocumentGroup
        heading={TIER_2_HEADING}
        emptyMessage={TIER_2_EMPTY}
        documents={tierTwo}
        openingId={openingId}
        onOpen={onOpen}
      />

      <NdaModal
        open={ndaOpen}
        ndaVersion={data.ndaVersion}
        busy={busy}
        onAccept={onAcceptNda}
        onCancel={onDismissNda}
      />
    </>
  );
}
